import { useState } from "react";
import { Heart, Clock, ArrowRight, Play } from "@phosphor-icons/react";
import { TRAIN_VIDEO, EPISODES } from "@/data/feelingsEpisodes";
import { KidsVideoPlayer } from "@/components/KidsVideoPlayer";

// Feelings Station feature band on the landing page — reuses the existing train clip, episode image and player.
const CARD = "relative rounded-[1.5rem] overflow-hidden bg-black shadow-[0_12px_30px_rgba(61,35,120,0.18)]";

const SneakPeekCard = ({ ep }) => {
  const [started, setStarted] = useState(false);
  return (
    <div className={CARD} data-testid="feature-sneak-peek">
      {started ? (
        <KidsVideoPlayer
          src={ep.sneakPeek.src}
          poster={ep.sneakPeek.poster}
          captions={ep.sneakPeek.captions}
          title={`${ep.title} — musical sneak peek`}
          playLabel={ep.title}
          autoPlay
          testid="feature-sneak-peek-player"
        />
      ) : (
        <div className="relative aspect-video">
          <img src={ep.thumbnail} alt={`${ep.title}: Mom, Lumi and a girl on a sunny sidewalk.`} className="absolute inset-0 w-full h-full object-contain bg-[#efe9fb]" loading="lazy" data-testid="feature-sneak-peek-thumb" />
          <button
            type="button"
            onClick={() => setStarted(true)}
            className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/20 transition-colors focus-visible:outline focus-visible:outline-4 focus-visible:outline-[#b79cf0]"
            aria-label={`Play the musical sneak peek of ${ep.title}`}
            data-testid="feature-sneak-peek-overlay-btn"
          >
            <span className="w-16 h-16 rounded-full bg-white/90 border-4 border-[#6a3fc7] flex items-center justify-center shadow-lg"><Play size={28} weight="fill" className="text-[#6a3fc7] ml-1" /></span>
          </button>
          <p className="absolute left-4 bottom-[4.25rem] sm:bottom-4 sm:right-[52%] font-display font-bold text-lg sm:text-xl text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)] pointer-events-none leading-tight">{ep.title}</p>
          <button
            type="button"
            onClick={() => setStarted(true)}
            className="absolute right-3 bottom-3 inline-flex items-center gap-2 h-11 px-4 rounded-full bg-[#3d2378] hover:bg-[#2d1a5c] text-white font-nunito font-bold text-sm shadow-md transition-colors focus-visible:ring-4 focus-visible:ring-[#b79cf0]"
            data-testid="feature-sneak-peek-btn"
          >
            Watch the musical sneak peek <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      )}
      <span className="absolute right-3 top-3 inline-flex items-center gap-1.5 bg-[#8b5cf6] text-white font-nunito font-bold text-xs sm:text-sm rounded-full px-3 py-1.5 shadow-md pointer-events-none" data-testid="feature-coming-soon">
        <Clock size={14} weight="bold" /> {ep.statusLabel}
      </span>
    </div>
  );
};

export const KidsFeelingsFeature = ({ onOpen }) => (
  <section className="mt-12 rounded-[2rem] bg-gradient-to-br from-[#efe6ff] to-[#e9e3fb] border border-[#e2d8f7] p-5 sm:p-8" aria-labelledby="feelings-feature-title" data-testid="kids-feelings-feature">
    <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
      <div className="flex items-start gap-3">
        <span className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center shrink-0"><Heart size={26} weight="fill" className="text-[#e75480]" /></span>
        <div>
          <h2 id="feelings-feature-title" className="font-display font-extrabold text-3xl sm:text-4xl text-[#3d2378] leading-none">Feelings Station <span aria-hidden="true" className="text-[#f2b544] text-lg">✦</span></h2>
          <p className="font-nunito text-base text-[#5b5572] mt-1.5">Stories, songs, and everyday adventures with Lumi.</p>
        </div>
      </div>
      <button type="button" onClick={onOpen} className="inline-flex items-center gap-2 font-nunito font-bold text-sm text-[#4a2e8a] bg-white border border-[#d9ccf5] rounded-full px-4 py-2 hover:bg-[#f3eefc] focus-visible:ring-4 focus-visible:ring-[#b79cf0]" data-testid="feelings-feature-open-btn">
        Open Feelings Station <ArrowRight size={16} weight="bold" />
      </button>
    </div>
    <div className="grid md:grid-cols-2 gap-5">
      <div className={CARD} data-testid="feature-train">
        <KidsVideoPlayer src={TRAIN_VIDEO.src} poster={TRAIN_VIDEO.poster} captions={TRAIN_VIDEO.captions} title={TRAIN_VIDEO.title} playLabel={TRAIN_VIDEO.title} testid="feature-train-player" />
      </div>
      {EPISODES.filter((e) => e.sneakPeek).map((ep) => <SneakPeekCard key={ep.id} ep={ep} />)}
    </div>
  </section>
);
